import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Input, message, Space, Popconfirm, Card, Divider, Checkbox, Upload } from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, SendOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import api from '../utils/api';

import { renderInputComponent } from '../utils/renderInputComponent';

const EditableDataTable = ({ dataType, fields, submissions, onRefresh, user, departmentId, editingSubmissionId, enableProofUpload = false }) => {
  const [dataSource, setDataSource] = useState([]);
  const [editingKey, setEditingKey] = useState('');
  const [editingCell, setEditingCell] = useState(''); // 格式: 'key-fieldKey'
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  // 证明材料上传结果（博士授权点使用）
  const [proofMaterials, setProofMaterials] = useState([]); // [{ upload_id, filename, file_type }]
  const autoSaveTimerRef = useRef(null);
  const focusRestoreRef = useRef(null); // 用于恢复焦点的引用
  const isUpdatingRef = useRef(false); // 标记是否正在更新数据源
  const submittedIdsRef = useRef(new Set()); // 记录已提交的记录ID，避免重新加载
  
  // 清理定时器
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // 创建空行
  const createEmptyRow = React.useCallback(() => ({
    key: `new-${Date.now()}-${Math.random()}`,
    isNew: true,
    isDraft: false, // 新行，还未保存
    ...fields.reduce((acc, field) => {
      if (!field.computed) {
        acc[field.key] = undefined;
      }
      return acc;
    }, {})
  }), [fields]);

  // 当部门或数据类型切换时，重置表格数据，避免上一部门/数据类型的残留草稿
  useEffect(() => {
    // 清空已提交ID缓存
    submittedIdsRef.current = new Set();
    // 重置数据源为仅包含一个空行
    setDataSource([createEmptyRow()]);
    setEditingKey('');
    setEditingCell('');
    setHasChanges(false);
  }, [departmentId, dataType, createEmptyRow]);

  useEffect(() => {
    // 如果是编辑已拒绝的记录，只加载该记录
    if (editingSubmissionId && submissions && submissions.length > 0) {
      const editingSub = submissions.find(s => s.id === editingSubmissionId);
      if (editingSub) {
        setDataSource([{
          key: `editing-${editingSub.id}`,
          id: editingSub.id,
            isNew: false,
          isDraft: true, // 标记为草稿，表示正在编辑
          ...editingSub.data_content,
          submitted_at: editingSub.submitted_at,
          status: editingSub.status
        }].map(record => calculateComputedFields(record)));
        setHasChanges(false);
        return;
      }
      }
      
    // 数据录入页面不应该显示已提交的数据（包括pending状态）
    // 数据录入页面只用于创建新数据，已提交的数据应该在"历史记录"页面查看
    // 只保留现有的草稿数据（没有id的记录，即新填写但未提交的数据）
    setDataSource(prevData => {
      // 只保留草稿数据（没有id的记录）
      const existingDrafts = prevData.filter(item => !item.id);
      
      // 确保至少有一个空行
      const hasEmptyRow = existingDrafts.some(item => {
        if (!item.isNew) return false;
        return !fields.some(field => {
          if (field.computed) return false;
          const val = item[field.key];
          return val !== undefined && val !== null && val !== '';
        });
      });
      
      if (!hasEmptyRow) {
        return [...existingDrafts, createEmptyRow()];
      }
      
      return existingDrafts;
    });
    setHasChanges(false);
  }, [submissions, fields, createEmptyRow, editingSubmissionId]);

  const isEditing = (record, fieldKey) => {
    return editingCell === `${record.key}-${fieldKey}`;
  };

  const startEditing = (record, fieldKey) => {
    if (record.status === 'approved') return; // 已审核的数据不能编辑
    const cellKey = `${record.key}-${fieldKey}`;
    setEditingCell(cellKey);
    setEditingKey(record.key);
  };

  const stopEditing = () => {
    setEditingCell('');
    setEditingKey('');
    focusRestoreRef.current = null;
  };
  
  // 处理Tab键导航到下一个单元格
  const handleTabNavigation = (currentRecord, currentFieldKey, shiftKey = false) => {
    const editableFields = fields.filter(f => !f.computed);
    const currentFieldIndex = editableFields.findIndex(f => f.key === currentFieldKey);
    
    if (currentFieldIndex === -1) return;
    
    let nextFieldIndex;
    let nextRecord = currentRecord;
    
    if (shiftKey) {
      // Shift+Tab: 向前导航
      if (currentFieldIndex > 0) {
        // 同一行的上一列
        nextFieldIndex = currentFieldIndex - 1;
      } else {
        // 上一行的最后一列
        const currentRecordIndex = dataSource.findIndex(r => r.key === currentRecord.key);
        if (currentRecordIndex > 0) {
          nextRecord = dataSource[currentRecordIndex - 1];
          nextFieldIndex = editableFields.length - 1;
        } else {
          return; // 已经是第一行第一列，无法向前
        }
      }
    } else {
      // Tab: 向后导航
      if (currentFieldIndex < editableFields.length - 1) {
        // 同一行的下一列
        nextFieldIndex = currentFieldIndex + 1;
      } else {
        // 下一行的第一列
        const currentRecordIndex = dataSource.findIndex(r => r.key === currentRecord.key);
        if (currentRecordIndex < dataSource.length - 1) {
          nextRecord = dataSource[currentRecordIndex + 1];
          nextFieldIndex = 0;
        } else {
          return; // 已经是最后一行最后一列，无法向后
        }
      }
    }
    
    const nextField = editableFields[nextFieldIndex];
    if (nextField && nextRecord.status !== 'approved') {
      // 停止当前编辑
      stopEditing();
      // 开始编辑下一个单元格
      setTimeout(() => {
        startEditing(nextRecord, nextField.key);
        focusRestoreRef.current = `${nextRecord.key}-${nextField.key}`;
      }, 0);
    }
  };
  
  // 恢复焦点到正在编辑的单元格
  useEffect(() => {
    if (editingCell && focusRestoreRef.current === editingCell) {
      // 延迟恢复焦点，确保DOM已更新
      const timer = setTimeout(() => {
        // 查找对应的输入元素
        const cellSelector = `input[data-cell-key="${editingCell}"], textarea[data-cell-key="${editingCell}"]`;
        const inputElement = document.querySelector(cellSelector);
        if (inputElement) {
          // 只有当焦点不在该元素上时才恢复焦点
          if (document.activeElement !== inputElement && !inputElement.contains(document.activeElement)) {
            inputElement.focus();
            // 如果是文本输入框，将光标移到末尾
            if (inputElement.setSelectionRange && inputElement.value) {
              const len = inputElement.value.length;
              inputElement.setSelectionRange(len, len);
            }
          }
        }
        // 清除恢复标记
        focusRestoreRef.current = null;
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [editingCell, dataSource]);

  // 确保最后有一个空行
  const ensureEmptyRow = () => {
    const lastRow = dataSource[dataSource.length - 1];
    if (!lastRow || !lastRow.isNew || Object.values(lastRow).some((val, idx) => {
      const keys = Object.keys(lastRow);
      const key = keys[idx];
      // 排除key、isNew等系统字段
      if (key === 'key' || key === 'isNew' || key === 'id' || key === 'status' || key === 'submitted_at') {
        return false;
      }
      return val !== undefined && val !== null && val !== '';
    })) {
      // 如果最后一行不是空行，添加一个空行
      setDataSource([...dataSource, createEmptyRow()]);
    }
  };

  const deleteRow = (key) => {
    const record = dataSource.find(item => item.key === key);
    
    if (!record) return;

    // 判断是否为“占位用”的空新行：isNew 且所有非系统字段都为空
    const isPlaceholderEmptyNewRow = record.isNew && fields.every(field => {
      if (field.computed) return true;
      const value = record[field.key];
      return value === undefined || value === null || value === '';
    });

    if (isPlaceholderEmptyNewRow) {
      // 占位空行用于继续录入，不允许删除，避免用户误删导致无法继续新增
      message.warning('最后的空行用于继续录入，不能删除。');
      return;
    }
    
    // 如果只有一行，清空该行内容而不是删除
    if (dataSource.length === 1) {
      const clearedRow = createEmptyRow();
      // 保留原有的key，这样不会触发重新渲染问题
      clearedRow.key = record.key;
      setDataSource([clearedRow]);
      setHasChanges(true);
      return;
    }
    
    // 多行时，正常删除
    const newData = dataSource.filter(item => item.key !== key);
    setDataSource(newData);
    setHasChanges(true);
    
    // 如果是已提交的记录（有id且不是草稿），需要删除后端数据
    if (record.id && !record.isDraft) {
      // 先标记为已删除，避免重新加载
      submittedIdsRef.current.add(record.id);
      handleDeleteRecord(record.id).then(() => {
        // 删除成功后刷新数据
        onRefresh();
      }).catch(() => {
        // 删除失败时，从缓存中移除，允许重新加载
        submittedIdsRef.current.delete(record.id);
      });
    }
  };

  // 清空全部数据
  const clearAll = async () => {
    // 收集所有已提交的记录ID（需要删除后端数据）
    const submittedRecords = dataSource.filter(item => item.id && !item.isDraft);
    
    // 先标记这些记录ID为已删除，避免useEffect重新加载它们
    submittedRecords.forEach(record => {
      if (record.id) {
        submittedIdsRef.current.add(record.id);
      }
    });
    
    // 立即清空所有数据，只保留一行空行
    const emptyRow = createEmptyRow();
    setDataSource([emptyRow]);
    setHasChanges(true);
    
    // 异步删除所有已提交的记录（不等待完成，避免阻塞UI）
    // 删除操作会在后台进行，即使失败也不会影响UI状态
    Promise.all(
      submittedRecords.map(record => 
        record.id ? handleDeleteRecord(record.id).catch(err => {
          console.error('删除记录失败:', err);
          // 删除失败时，从缓存中移除，允许重新加载
          submittedIdsRef.current.delete(record.id);
        }) : Promise.resolve()
      )
    ).then(() => {
      // 删除完成后刷新数据（但不会重新加载已标记为已删除的记录）
      onRefresh();
    });
    
    message.success('已清空所有数据');
  };

  // 获取所有草稿记录
  const getDraftRecords = () => {
    return dataSource.filter(record => {
      // 检查是否有数据（至少有一个字段有值）
      const hasData = fields.some(field => {
        if (field.computed) return false;
        const value = record[field.key];
        return value !== undefined && value !== null && value !== '';
      });
      
      // 如果没有数据，不显示（排除空行）
      if (!hasData) {
        return false;
      }
      
      // 草稿记录的条件：
      // 1. 没有id（未提交到后端）
      // 2. 或者明确标记为isDraft: true
      const hasNoId = !record.id;
      const isMarkedDraft = record.isDraft === true;
      const isDraft = hasNoId || isMarkedDraft;
      
      return isDraft && hasData;
    });
  };

  const draftRecords = getDraftRecords();

  // 上传证明材料（图片 / PDF / 压缩包等），仅在 enableProofUpload 时使用
  // 注意：一次只处理单个文件，避免 antd Upload 多次触发导致重复
  const handleUploadProofMaterials = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('files', file);
    
    try {
      const response = await api.post('/proof-materials/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      const { upload_id, files } = response.data || {};
      if (!upload_id || !files || !files.length) {
        message.error('上传失败：返回数据不完整');
        return;
      }
      const mapped = files.map(f => ({
        upload_id,
        filename: f.filename,
        file_type: f.file_type || '',
        remark: ''
      }));
      // 去重：同一 upload_id + filename 只保留一条
      setProofMaterials(prev => {
        const combined = [...prev, ...mapped];
        const seen = new Set();
        const deduped = [];
        for (const item of combined) {
          const key = `${item.upload_id}::${item.filename}`;
          if (seen.has(key)) continue;
          seen.add(key);
          deduped.push(item);
        }
        return deduped;
      });
      message.success('证明材料上传成功');
    } catch (error) {
      console.error('上传证明材料失败:', error);
      message.error(error.response?.data?.error || '上传证明材料失败');
    }
  };

  // 提交整个表格的所有草稿数据（使用批量API一次性提交）
  const handleTableSubmit = async () => {
    if (draftRecords.length === 0) {
      message.warning('没有待提交的数据');
      return;
    }

    // 逐行校验必填字段，避免不完整的数据被批量提交
    const invalidRows = [];
    draftRecords.forEach((record, index) => {
      for (const field of fields) {
        if (field.required && !field.computed) {
          const value = record[field.key];
          if (value === undefined || value === null || value === '') {
            invalidRows.push({
              rowIndex: index + 1, // 行号从1开始，便于提示
              fieldLabel: field.label
            });
          }
        }
      }
    });

    if (invalidRows.length > 0) {
      // 只提示一次，避免信息过多
      const firstError = invalidRows[0];
      message.error(`第 ${firstError.rowIndex} 行的「${firstError.fieldLabel}」未填写完整，请补全所有必填项后再提交。`);
      return;
    }

    if (enableProofUpload && (!proofMaterials || proofMaterials.length === 0)) {
      message.error('博士授权点须先上传证明材料后方可提交审核');
      return;
    }

    setSubmitting(true);
    try {
      // 如果是编辑已拒绝的记录，使用更新API
      if (editingSubmissionId && draftRecords.length === 1 && draftRecords[0].id === editingSubmissionId) {
        const record = draftRecords[0];
        const recordData = { ...record };
        delete recordData.key; // 移除前端生成的key
        delete recordData.id; // 移除id，因为它在URL中
        delete recordData.isNew;
        delete recordData.isDraft;
        delete recordData.submitted_at;
        delete recordData.status;

        // 使用更新API，将状态改为pending
        await api.put(`/submissions/${editingSubmissionId}`, {
          data_content: recordData,
          status: 'pending' // 重新提交后状态转为待审核
        });

        message.success('数据已更新，状态已转为待审核');
        
        // 刷新数据
        if (onRefresh) {
          onRefresh();
        }
        
        setSubmitting(false);
        return;
      }

      // 获取统一的时间戳，确保同一批次的所有记录使用相同的时间
      const batchSubmittedAt = new Date().toISOString();

      // 准备批量提交的数据
      const submissionsData = draftRecords.map(record => {
        const recordData = { ...record };
        delete recordData.key; // 移除前端生成的key
        return {
          department_id: departmentId,
          data_type: dataType,
          data_content: recordData,
          year: recordData.year || null
        };
      });

      // 使用批量提交API一次性提交
      const payload = {
        submissions: submissionsData,
        submitted_at: batchSubmittedAt
      };
      // 如果启用证明材料上传，将上传结果一并提交（仅博士授权点等场景）
      if (enableProofUpload && proofMaterials.length > 0) {
        payload.proof_materials = proofMaterials;
      }

      const response = await api.post('/submissions/batch-create', payload);

      const submittedIds = [];
      if (response.data && response.data.submissions) {
        response.data.submissions.forEach(sub => {
          if (sub.id) {
            submittedIds.push(sub.id);
          }
        });
      }
      
      // 记录所有已提交的ID，避免重新加载
      submittedIds.forEach(id => submittedIdsRef.current.add(id));

      if (response.data && response.data.created_count > 0) {
        message.success(`成功提交 ${response.data.created_count} 条数据`);
      } else {
        message.warning('批量提交完成，但未创建任何记录');
      }

      if (response.data && response.data.errors && response.data.errors.length > 0) {
        console.warn('部分记录提交失败:', response.data.errors);
      }

      // 提交成功后，立即从dataSource中移除已提交的记录
      const submittedKeys = draftRecords.map(r => r.key);
      
      setDataSource(prevData => {
        const filtered = prevData.filter(item => !submittedKeys.includes(item.key));
        // 确保至少有一个空行
        const hasEmptyRow = filtered.some(item => {
          if (!item.isNew) return false;
          return !fields.some(field => {
            if (field.computed) return false;
            const val = item[field.key];
            return val !== undefined && val !== null && val !== '';
          });
        });
        if (!hasEmptyRow) {
          return [...filtered, createEmptyRow()];
        }
        return filtered;
      });
      
      // 清空已关联的证明材料（避免下次提交重复关联）
      if (enableProofUpload) {
        setProofMaterials([]);
      }
      
      // 刷新数据
      onRefresh();
    } catch (error) {
      console.error('提交失败:', error);
      message.error('提交过程中出现错误');
    } finally {
      setSubmitting(false);
    }
  };

  // 提交审核，返回新创建的记录ID（如果有）
  const submitForReview = async (key, silent = false, submittedAt = null) => {
    try {
      const row = dataSource.find(item => item.key === key);
      if (!row) return;

      // 验证必填字段
      for (const field of fields) {
        if (field.required && !field.computed) {
          const value = row[field.key];
          if (value === undefined || value === null || value === '') {
            message.error(`请填写${field.label}后再提交`);
            return;
          }
        }
      }

      // 构建数据内容（包含computed字段）
      const dataContent = {};
      fields.forEach(field => {
          let value = row[field.key];
        
        // 如果是computed字段，使用formula计算
        if (field.computed && field.formula) {
          value = field.formula(row);
        }
          
          // 处理日期类型
          if (field.type === 'date' && value) {
            if (moment.isMoment(value)) {
              value = value.format('YYYY-MM-DD');
            }
          }
          
          // 处理日期范围类型
          if (field.type === 'daterange' && value && Array.isArray(value)) {
            if (value[0] && moment.isMoment(value[0])) {
              value = [
                value[0].format('YYYY-MM-DD'),
                value[1] ? value[1].format('YYYY-MM-DD') : ''
              ];
            }
          }
          
          dataContent[field.key] = value;
      });

      setSubmitting(true);

      let newSubmissionId = null;
      if (row.id && !row.isDraft) {
        // 更新已提交的记录（这种情况不应该发生，但保留以防万一）
        await api.put(`/submissions/${row.id}`, {
          data_content: dataContent
        });
        message.success('更新成功');
        newSubmissionId = row.id;
      } else {
        // 创建新记录并提交审核
        // 从dataContent中提取year字段（支持'year'和'年份'两种字段名）
        let submissionYear = dataContent.year || dataContent['年份'];
        // 如果year是字符串，转换为数字
        if (submissionYear && typeof submissionYear === 'string') {
          submissionYear = parseInt(submissionYear, 10);
        }
        // 如果year无效，使用当前年份
        if (!submissionYear || isNaN(submissionYear) || submissionYear < 1900 || submissionYear > 2100) {
          submissionYear = new Date().getFullYear();
        }
        
        const requestData = {
          department_id: departmentId,
          data_type: dataType,
          data_content: dataContent,
          year: submissionYear
        };
        
        // 如果提供了统一的时间戳，使用它（用于批次提交）
        if (submittedAt) {
          requestData.submitted_at = submittedAt;
        }
        
        const response = await api.post('/submissions', requestData);
        // 检查是否有覆盖信息
        if (response.data && response.data.replaced_count > 0) {
          message.success(`提交审核成功，已覆盖 ${response.data.replaced_count} 条同一年份的已审核记录`);
        } else {
          message.success('提交审核成功');
        }
        // 获取新创建的记录ID
        if (response.data && response.data.id) {
          newSubmissionId = response.data.id;
        }
      }

      // 提交成功后，立即从dataSource中移除已提交的记录
      const submittedRecord = dataSource.find(item => item.key === key);
      const recordId = newSubmissionId || (submittedRecord && submittedRecord.id);
      
      if (recordId) {
        // 记录已提交的ID，避免重新加载
        submittedIdsRef.current.add(recordId);
      }
      
      setDataSource(prevData => {
        const filtered = prevData.filter(item => item.key !== key);
        // 确保至少有一个空行
        const hasEmptyRow = filtered.some(item => {
          if (!item.isNew) return false;
          return !fields.some(field => {
            if (field.computed) return false;
            const val = item[field.key];
            return val !== undefined && val !== null && val !== '';
          });
        });
        if (!hasEmptyRow) {
          return [...filtered, createEmptyRow()];
        }
        return filtered;
      });
      
      // 刷新数据
      onRefresh();
      
      // 返回新创建的记录ID
      return { id: recordId };
    } catch (error) {
      console.error('提交失败:', error);
      message.error(error.response?.data?.error || '提交失败');
      throw error; // 重新抛出错误，让调用者处理
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRecord = async (id) => {
    try {
      await api.delete(`/submissions/${id}`);
      // 标记为已删除，避免重新加载
      submittedIdsRef.current.add(id);
      // 不在这里调用onRefresh，由clearAll统一处理
      return Promise.resolve();
    } catch (error) {
      // 删除失败时，从缓存中移除，允许重新加载
      submittedIdsRef.current.delete(id);
      return Promise.reject(error);
    }
  };

  const save = async (key, silent = false) => {
    console.log('save函数被调用, key:', key, 'silent:', silent);
    try {
      const row = dataSource.find(item => item.key === key);
      if (!row) return;

      // 验证必填字段
      for (const field of fields) {
        if (field.required && !field.computed) {
          const value = row[field.key];
          if (value === undefined || value === null || value === '') {
            message.error(`请填写${field.label}`);
            return;
          }
        }
      }

      // 构建数据内容（包含computed字段）
      const dataContent = {};
      fields.forEach(field => {
          let value = row[field.key];
        
        // 如果是computed字段，使用formula计算
        if (field.computed && field.formula) {
          value = field.formula(row);
        }
          
          // 处理日期类型
          if (field.type === 'date' && value) {
            if (moment.isMoment(value)) {
              value = value.format('YYYY-MM-DD');
            }
          }
          
          // 处理日期范围类型
          if (field.type === 'daterange' && value && Array.isArray(value)) {
            if (value[0] && moment.isMoment(value[0])) {
              value = [
                value[0].format('YYYY-MM-DD'),
                value[1] ? value[1].format('YYYY-MM-DD') : ''
              ];
            }
          }
          
          dataContent[field.key] = value;
      });

      setSaving(true);

      // 只更新本地数据，不提交到后端
      const newData = [...dataSource];
      const index = newData.findIndex(item => item.key === key);
      if (index > -1) {
        const updatedRecord = {
          ...newData[index],
          ...dataContent,
          isDraft: true, // 标记为草稿
          isNew: false // 已保存，不再是新行
        };
        // 确保保留key和其他系统字段
        updatedRecord.key = newData[index].key;
        newData[index] = updatedRecord;
        setDataSource(newData);
        if (!silent) message.success('保存成功（未提交审核）');
        
        // 调试信息
        console.log('保存后的记录:', updatedRecord);
        console.log('isDraft:', updatedRecord.isDraft);
        console.log('id:', updatedRecord.id);
      }

      stopEditing();
      setHasChanges(false);
      setSaving(false);
      
      // 确保最后有一个空行（使用函数式更新）
      setDataSource(prevData => {
        const lastRow = prevData[prevData.length - 1];
        if (!lastRow) {
          return [...prevData, createEmptyRow()];
        }
        
        // 检查最后一行是否是空行
        const lastRowIsEmpty = lastRow.isNew === true && !fields.some(field => {
          if (field.computed) return false;
          const val = lastRow[field.key];
          return val !== undefined && val !== null && val !== '';
        });
        
        if (!lastRowIsEmpty) {
          // 最后一行不是空行，添加一个空行
          return [...prevData, createEmptyRow()];
        }
        return prevData;
      });
    } catch (error) {
      console.error('保存失败:', error);
      message.error(error.response?.data?.error || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  // 计算computed字段的值
  const calculateComputedFields = (record) => {
    const computedFields = fields.filter(f => f.computed && f.formula);
    computedFields.forEach(field => {
      if (field.formula) {
        record[field.key] = field.formula(record);
      }
    });
    return record;
  };

  const handleCellChange = (key, fieldKey, value, autoSave = false) => {
    // 如果正在编辑这个单元格，标记需要恢复焦点
    const cellKey = `${key}-${fieldKey}`;
    if (editingCell === cellKey) {
      focusRestoreRef.current = cellKey;
    }
    
    setDataSource(prevData => {
      const newData = [...prevData];
      const index = newData.findIndex(item => key === item.key);
      if (index > -1) {
        newData[index][fieldKey] = value;
        
        // 自动计算computed字段
        newData[index] = calculateComputedFields(newData[index]);
        
        // 如果是在空行输入，自动进入编辑模式
        if (newData[index].isNew && editingKey !== key) {
          setEditingKey(key);
          setEditingCell(`${key}-${fieldKey}`);
          focusRestoreRef.current = `${key}-${fieldKey}`;
        }
        
        // 只在当前行是最后一行，且当前行有数据时，才检查是否需要添加新空行
        // 避免在每次输入时都添加新行
        if (index === newData.length - 1) {
          // 检查当前行（最后一行）是否有数据
          const currentRowHasData = fields.some(field => {
            if (field.computed) return false;
            const val = newData[index][field.key];
            return val !== undefined && val !== null && val !== '';
          });
          
          // 如果最后一行有数据，添加一个新空行
          if (currentRowHasData) {
            // 检查是否已经有空行（避免重复添加）
            const hasEmptyRow = newData.some(row => {
              if (!row.isNew) return false;
              return !fields.some(field => {
                if (field.computed) return false;
                const val = row[field.key];
                return val !== undefined && val !== null && val !== '';
              });
            });
            
            if (!hasEmptyRow) {
              newData.push(createEmptyRow());
            }
          }
        }
        
        return newData;
      }
      return prevData;
    });
    setHasChanges(true);
    
    // 如果启用自动保存，延迟保存（防抖）
    if (autoSave) {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      autoSaveTimerRef.current = setTimeout(() => {
        // 使用最新的dataSource状态
        setDataSource(currentData => {
          const record = currentData.find(item => item.key === key);
          if (!record) return currentData;
          
          // 检查是否有必填字段未填写
          const hasRequiredEmpty = fields.some(field => {
            if (field.required && !field.computed) {
              const val = record[field.key];
              return val === undefined || val === null || val === '';
            }
            return false;
          });
          
          // 如果有必填字段未填写，不自动保存
          if (!hasRequiredEmpty) {
            // 直接调用save函数保存
            save(key, true).catch(err => {
              console.error('自动保存失败:', err);
            });
          }
          
          return currentData;
        });
      }, 1000); // 1秒后自动保存
    }
  };

  // 下载模板
  const handleDownloadTemplate = () => {
    try {
      // 创建工作簿
      const wb = XLSX.utils.book_new();
      
      // 构建表头 - 按照fields的顺序
      const headers = ['序号'];
      
      // 检查是否有年份字段
      const hasYearField = fields.some(f => (f.key === 'year' || f.key === '年份') && !f.computed);
      if (hasYearField) {
        headers.push('年份');
      }
      
      // 按照fields的顺序添加字段（包含computed字段，但跳过年份字段）
      const fieldKeys = [];
      fields.forEach(field => {
        // 跳过年份字段（已经在表头中）
        if (field.key !== 'year' && field.key !== '年份') {
          headers.push(field.label);
          fieldKeys.push(field.key);
        }
      });
      
      // 创建工作表数据
      const wsData = [headers];
      
      // 创建工作表
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      
      // 设置列宽
      const colWidths = headers.map(() => ({ wch: 15 }));
      ws['!cols'] = colWidths;
      
      // 添加到工作簿
      XLSX.utils.book_append_sheet(wb, ws, '数据模板');
      
      // 生成文件名
      const formConfig = fields.length > 0 ? fields[0] : null;
      const formLabel = formConfig ? (formConfig.label || dataType) : dataType;
      const filename = `${formLabel}_导入模板_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
      
      // 导出文件
      XLSX.writeFile(wb, filename);
      
      message.success('模板下载成功');
    } catch (error) {
      console.error('下载模板失败:', error);
      message.error('下载模板失败');
    }
  };

  // 处理Excel导入
  const handleImport = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          let workbook;
          try {
            workbook = XLSX.read(data, { type: 'array' });
          } catch (error) {
            message.error('无法读取Excel文件，请确保文件格式正确（.xlsx 或 .xls）');
            reject(new Error('Invalid file format'));
            return;
          }
          
          if (!workbook || !workbook.SheetNames || workbook.SheetNames.length === 0) {
            message.error('Excel文件中没有工作表');
            reject(new Error('No worksheets'));
            return;
          }
          
          // 读取第一个工作表
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          if (!worksheet) {
            message.error('无法读取工作表数据');
            reject(new Error('Cannot read worksheet'));
            return;
          }
          
          // 转换为JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
          
          if (!jsonData || jsonData.length < 2) {
            message.error('Excel文件格式错误：至少需要表头和数据行。请使用下载的模板文件。');
            reject(new Error('Invalid format: need header and data rows'));
            return;
          }
          
          // 第一行是表头
          const headers = jsonData[0];
          
          if (!headers || headers.length === 0) {
            message.error('Excel文件表头为空，请使用下载的模板文件。');
            reject(new Error('Empty headers'));
            return;
          }
          
          // 检查是否有年份字段
          const hasYearField = headers.includes('年份');
          const yearIndex = hasYearField ? headers.indexOf('年份') : -1;
          
          // 构建字段映射（表头 -> 字段key）
          const fieldMap = {};
          const missingFields = [];
          
          fields.forEach(field => {
            if (!field.computed) {
              const headerIndex = headers.indexOf(field.label);
              if (headerIndex !== -1) {
                fieldMap[headerIndex] = field.key;
              } else {
                // 检查年份字段的特殊处理
                if (field.key === 'year' || field.key === '年份') {
                  if (hasYearField) {
                    fieldMap[yearIndex] = field.key;
                  } else {
                    missingFields.push(field.label);
                  }
                } else {
                  missingFields.push(field.label);
                }
              }
            }
          });
          
          // 检查是否匹配到足够的字段
          const matchedFieldsCount = Object.keys(fieldMap).length;
          const expectedFieldsCount = fields.filter(f => !f.computed).length;
          
          if (matchedFieldsCount === 0) {
            message.error('Excel文件表头与当前表单不匹配。请使用下载的模板文件，并确保表头列名正确。');
            reject(new Error('No matching fields'));
            return;
          }
          
          if (matchedFieldsCount < expectedFieldsCount * 0.5) {
            message.warning(`警告：Excel文件中只匹配到 ${matchedFieldsCount}/${expectedFieldsCount} 个字段。部分数据可能无法导入。`);
          }
          
          // 解析数据行（跳过表头）
          const importedRows = [];
          const errorRows = [];
          
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            
            // 跳过空行
            if (!row || row.every(cell => cell === '' || cell === null || cell === undefined)) {
              continue;
            }
            
            try {
              // 构建数据对象
              const rowData = {
                key: `imported-${Date.now()}-${i}-${Math.random()}`,
                isNew: true,
                isDraft: false
              };
              
              // 处理年份字段
              if (hasYearField && yearIndex !== -1 && row[yearIndex]) {
                const yearValue = row[yearIndex];
                // 查找年份字段
                const yearField = fields.find(f => (f.key === 'year' || f.key === '年份') && !f.computed);
                if (yearField) {
                  const parsedYear = typeof yearValue === 'number' ? yearValue : parseInt(yearValue, 10);
                  if (!isNaN(parsedYear) && parsedYear >= 1900 && parsedYear <= 2100) {
                    rowData[yearField.key] = parsedYear;
                  }
                }
              }
              
              // 处理其他字段
              Object.keys(fieldMap).forEach(headerIndex => {
                const fieldKey = fieldMap[headerIndex];
                const cellValue = row[parseInt(headerIndex, 10)];
                
                if (cellValue !== '' && cellValue !== null && cellValue !== undefined) {
                  const field = fields.find(f => f.key === fieldKey);
                  if (field) {
                    try {
                      // 根据字段类型转换值
                      if (field.type === 'number') {
                        const numValue = typeof cellValue === 'number' ? cellValue : parseFloat(cellValue);
                        if (!isNaN(numValue)) {
                          rowData[fieldKey] = numValue;
                        }
                      } else if (field.type === 'date') {
                        // 尝试解析日期
                        if (typeof cellValue === 'string') {
                          // 字符串日期，直接使用
                          rowData[fieldKey] = cellValue;
                        } else if (typeof cellValue === 'number') {
                          // Excel日期序列号（从1900-01-01开始的天数）
                          const excelEpoch = new Date(1899, 11, 30); // 1899-12-30
                          const date = new Date(excelEpoch.getTime() + cellValue * 24 * 60 * 60 * 1000);
                          if (!isNaN(date.getTime())) {
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            rowData[fieldKey] = `${year}-${month}-${day}`;
                          } else {
                            rowData[fieldKey] = cellValue.toString();
                          }
                        } else {
                          rowData[fieldKey] = cellValue;
                        }
                      } else {
                        rowData[fieldKey] = cellValue;
                      }
                    } catch (fieldError) {
                      // 字段解析错误，跳过该字段
                      console.warn(`解析字段 ${field.label} 失败:`, fieldError);
                    }
                  }
                }
              });
              
              // 计算computed字段
              const computedRecord = calculateComputedFields(rowData);
              
              // 只添加至少有一个非空字段的行
              const hasData = Object.keys(computedRecord).some(key => 
                key !== 'key' && key !== 'isNew' && key !== 'isDraft' && 
                computedRecord[key] !== undefined && computedRecord[key] !== null && computedRecord[key] !== ''
              );
              
              if (hasData) {
                importedRows.push(computedRecord);
              }
            } catch (rowError) {
              console.warn(`解析第 ${i + 1} 行数据失败:`, rowError);
              errorRows.push(i + 1);
            }
          }
          
          if (importedRows.length === 0) {
            let errorMsg = 'Excel文件中没有有效数据。';
            if (errorRows.length > 0) {
              errorMsg += ` 第 ${errorRows.join(', ')} 行数据格式错误。`;
            }
            errorMsg += ' 请检查数据格式，或使用下载的模板文件。';
            message.error(errorMsg);
            reject(new Error('No valid data'));
            return;
          }
          
          // 将导入的数据添加到dataSource
          setDataSource(prevData => {
            // 保留现有数据，添加导入的数据
            const newData = [...prevData];
            
            // 移除最后一个空行（如果有）
            const lastRow = newData[newData.length - 1];
            const isEmptyRow = lastRow && lastRow.isNew && !fields.some(field => {
              if (field.computed) return false;
              const val = lastRow[field.key];
              return val !== undefined && val !== null && val !== '';
            });
            
            if (isEmptyRow) {
              newData.pop();
            }
            
            // 添加导入的数据
            newData.push(...importedRows);
            
            // 确保最后有一个空行
            newData.push(createEmptyRow());
            
            return newData;
          });
          
          setHasChanges(true);
          
          let successMsg = `成功导入 ${importedRows.length} 条数据`;
          if (errorRows.length > 0) {
            successMsg += `，${errorRows.length} 行数据格式错误已跳过`;
          }
          message.success(successMsg);
          resolve();
        } catch (error) {
          console.error('导入失败:', error);
          let errorMsg = '导入失败：';
          if (error.message) {
            errorMsg += error.message;
          } else if (error.toString && error.toString() !== '[object Object]') {
            errorMsg += error.toString();
          } else {
            errorMsg += '文件格式错误或数据不匹配。请使用下载的模板文件。';
          }
          message.error(errorMsg);
          reject(error);
        }
      };
      
      reader.onerror = () => {
        const errorMsg = '读取文件失败，请确保文件未被占用且格式正确。';
        message.error(errorMsg);
        reject(new Error('File read error'));
      };
      
      try {
        reader.readAsArrayBuffer(file);
      } catch (error) {
        message.error('无法读取文件：' + (error.message || '未知错误'));
        reject(error);
      }
    }).catch(error => {
      // 确保所有错误都被捕获，不会导致未处理的异常
      console.error('导入过程出错:', error);
      // 错误消息已经在上面显示，这里不需要再次显示
    });
  };

  const getInputComponent = (field, record) =>
    renderInputComponent(field, record, {
      handleCellChange,
      stopEditing,
      handleTabNavigation,
      editingCell,
      hasChanges,
    });

  const columns = [
    ...fields
      .map(field => ({
        title: field.label,
        dataIndex: field.key,
        key: field.key,
        width: 150,
        render: (text, record) => {
          // computed字段：只读显示，不可编辑
          if (field.computed) {
            // 实时计算computed字段的值
            const computedRecord = calculateComputedFields({ ...record });
            const computedValue = computedRecord[field.key];
            
            return (
              <span 
                style={{ 
                  padding: '4px 8px',
                  display: 'inline-block',
                  width: '100%',
                  minHeight: '22px',
                  backgroundColor: '#f5f5f5',
                  color: '#666',
                  cursor: 'not-allowed'
                }}
                title="自动计算字段，不可编辑"
              >
                {computedValue !== undefined && computedValue !== null && computedValue !== '' ? String(computedValue) : '-'}
              </span>
            );
          }
          
          // 非computed字段：可编辑
          // 如果正在编辑这个单元格，显示输入框
          const editing = isEditing(record, field.key);
          if (editing) {
            return getInputComponent(field, record);
          }
          
          // 显示值
          const value = record[field.key];
          const isEmpty = value === undefined || value === null || value === '';
          const canEdit = record.status !== 'approved';
          
          // 如果是空行，显示可点击的占位符
          if (record.isNew && isEmpty) {
            return (
              <span 
                style={{ color: '#ccc', cursor: 'pointer', padding: '4px 8px', display: 'inline-block', width: '100%' }}
                onClick={(e) => {
                  e.stopPropagation();
                  startEditing(record, field.key);
                }}
              >
                点击输入
              </span>
            );
          }
          
          if (isEmpty) {
            return (
              <span 
                style={{ 
                  color: '#ccc', 
                  cursor: canEdit ? 'pointer' : 'default',
                  padding: '4px 8px',
                  display: 'inline-block',
                  width: '100%'
                }}
                onClick={(e) => {
                  if (canEdit) {
                    e.stopPropagation();
                    startEditing(record, field.key);
                  }
                }}
              >
                -
              </span>
            );
          }
          
          let displayValue = '';
          if (field.type === 'date' && value) {
            displayValue = moment(value).format('YYYY-MM-DD');
          } else if (field.type === 'daterange' && Array.isArray(value)) {
            displayValue = `${value[0] || ''} ~ ${value[1] || ''}`;
          } else if (field.type === 'select' && field.options) {
            const option = field.options.find(opt => opt.value === value);
            displayValue = option ? option.label : value;
          } else {
            displayValue = String(value);
          }
          
          return (
            <span 
              onClick={(e) => {
                if (canEdit) {
                  e.stopPropagation();
                  startEditing(record, field.key);
                }
              }}
              style={{ 
                cursor: canEdit ? 'pointer' : 'default',
                padding: '4px 8px',
                display: 'inline-block',
                width: '100%',
                minHeight: '22px'
              }}
            >
              {displayValue}
            </span>
          );
        }
      })),
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => {
        // 已审核的数据不显示操作按钮
        if (record.status === 'approved') {
          return null;
        }
        
        // 只显示删除按钮，提交按钮移到表格下方
        return (
          <Popconfirm
            title="确定要删除这一行吗？"
            onConfirm={() => deleteRow(record.key)}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        );
      }
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownloadTemplate}
          >
            下载模板
          </Button>
          <Upload
            accept=".xlsx,.xls"
            showUploadList={false}
            beforeUpload={(file) => {
              // 验证文件类型
              const isValidType = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                                  file.type === 'application/vnd.ms-excel' ||
                                  file.name.endsWith('.xlsx') ||
                                  file.name.endsWith('.xls');
              
              if (!isValidType) {
                message.error('请选择Excel文件（.xlsx 或 .xls 格式）');
                return false;
              }
              
              // 验证文件大小（限制为10MB）
              const isLt10M = file.size / 1024 / 1024 < 10;
              if (!isLt10M) {
                message.error('文件大小不能超过10MB');
                return false;
              }
              
              // 执行导入
              handleImport(file).catch(error => {
                // 错误已经在handleImport中处理，这里只是确保不会抛出未捕获的异常
                console.error('导入过程出错:', error);
              });
              
              return false; // 阻止自动上传
            }}
          >
            <Button icon={<UploadOutlined />}>
              导入Excel
            </Button>
          </Upload>
          <Popconfirm
            title="确定要清空所有数据吗？此操作不可恢复！"
            onConfirm={clearAll}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
            >
              清空全部
            </Button>
          </Popconfirm>
          {enableProofUpload && (
            <Upload
              multiple
              showUploadList={false}
              beforeUpload={(file) => {
                // 手动上传到后端证明材料接口（单文件），避免重复
                handleUploadProofMaterials(file);
                return false; // 阻止 Upload 组件自动上传
              }}
            >
              <Button icon={<UploadOutlined />}>
                上传证明材料
              </Button>
            </Upload>
          )}
        </Space>
        {hasChanges && (
          <span style={{ color: '#ff4d4f' }}>有未保存的更改</span>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="key"
        pagination={false}
        scroll={{ x: 'max-content' }}
        bordered
        size="small"
        onRow={(record) => {
          // 点击行时，如果点击的不是单元格内容，则编辑第一个字段
          if (record.status !== 'approved') {
            return {
              onClick: (e) => {
                // 如果点击的是操作列，不处理
                if (e.target.closest('.ant-table-tbody td:last-child')) {
                  return;
                }
                // 如果点击的是单元格内的span（已经有点击事件），不处理，让单元格的点击事件处理
                if (e.target.tagName === 'SPAN' && e.target.closest('td')) {
                  return;
                }
                // 如果点击的不是输入框和span，编辑第一个可编辑字段
                if (!e.target.closest('input, textarea, .ant-select, .ant-picker, span')) {
                  const firstField = fields.find(f => !f.computed);
                  if (firstField && !isEditing(record, firstField.key)) {
                    startEditing(record, firstField.key);
                  }
                }
              }
            };
          }
          return {};
        }}
      />
      
      {/* 提交审核区域 - 始终显示 */}
      <Card 
        size="small" 
        style={{ marginTop: '16px', backgroundColor: '#f0f7ff' }}
        title={
          <span>
            <SendOutlined style={{ marginRight: '8px' }} />
            提交审核
            {draftRecords.length > 0 && (
              <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                (共 {draftRecords.length} 条待提交数据)
              </span>
            )}
          </span>
        }
      >
        {enableProofUpload && proofMaterials.length > 0 && (
          <div style={{ marginBottom: 16, padding: 12, border: '1px solid #e8e8e8', borderRadius: 4, background: '#fafafa' }}>
            <div style={{ marginBottom: 8, fontWeight: 'bold' }}>本次提交的证明材料</div>
            {proofMaterials.map((pm, index) => (
              <div key={`${pm.upload_id}-${pm.filename}-${index}`} style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                <span style={{ flex: '0 0 220px', wordBreak: 'break-all' }}>{pm.filename}</span>
                <Input
                  style={{ flex: 1 }}
                  placeholder="备注说明（可选）"
                  value={pm.remark || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setProofMaterials(prev => prev.map((item, i) => {
                      if (i !== index) return item;
                      return { ...item, remark: value };
                    }));
                  }}
                />
                <Button
                  danger
                  size="small"
                  onClick={() => {
                    setProofMaterials(prev => prev.filter((_, i) => i !== index));
                  }}
                >
                  删除
                </Button>
              </div>
            ))}
            <div style={{ fontSize: 12, color: '#999' }}>
              说明：上述证明材料会与本次提交的所有数据记录一并保存，管理员在审核时可查看并下载。
            </div>
          </div>
        )}
        {draftRecords.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
            <p>暂无待提交的数据</p>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px' }}>
            <div style={{ color: '#666', fontSize: '12px' }}>
              共 {draftRecords.length} 条待提交数据
            </div>
            <Button
              type="primary"
              size="large"
              icon={<SendOutlined />}
              onClick={handleTableSubmit}
              loading={submitting}
            >
              提交审核 ({draftRecords.length} 条)
            </Button>
          </div>
        )}
      </Card>

      <div style={{ marginTop: '16px', color: '#999', fontSize: '12px' }}>
        <p style={{ marginBottom: '4px' }}>提示：</p>
        <ul style={{ margin: 0, paddingLeft: '20px' }}>
          <li>点击任意单元格可直接编辑（已审核的数据除外）</li>
          <li>输入完成后按Enter键或点击其他位置自动保存</li>
          <li>保存后的数据可在下方提交审核</li>
        </ul>
      </div>
    </div>
  );
};

export default EditableDataTable;

